import { useState } from "react";
import type { VisitRecord } from "../../types/domain";
import {
  formatDateTime,
  toDateTimeLocalInputValue,
  zonedLocalInputToUtcIso,
} from "../../utils/dateTime";
import { Field } from "../common/Field";
import { Modal } from "../common/Modal";
import { StatusBanner } from "../common/StatusBanner";

type CloseVisitsModalProps = {
  records: VisitRecord[];
  isSaving: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onConfirm: (checkoutAtIso: string) => Promise<void>;
};

const MAX_LISTED_VISITORS = 8;
const FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

export function CloseVisitsModal({
  records,
  isSaving,
  errorMessage,
  onClose,
  onConfirm,
}: CloseVisitsModalProps) {
  const [checkoutAtLocal, setCheckoutAtLocal] = useState(() =>
    toDateTimeLocalInputValue(new Date()),
  );
  const [fieldError, setFieldError] = useState<string | null>(null);

  const latestCheckinAt = records.reduce(
    (latest, record) =>
      !latest || Date.parse(record.checkinAt) > Date.parse(latest)
        ? record.checkinAt
        : latest,
    "",
  );
  const listedRecords = records.slice(0, MAX_LISTED_VISITORS);
  const hiddenCount = records.length - listedRecords.length;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!checkoutAtLocal) {
      setFieldError("L'orario di uscita è obbligatorio.");
      return;
    }

    const checkoutAtIso = zonedLocalInputToUtcIso(checkoutAtLocal);
    const checkoutTime = Date.parse(checkoutAtIso);

    if (checkoutTime > Date.now() + FUTURE_TOLERANCE_MS) {
      setFieldError("L'orario di uscita non può essere nel futuro.");
      return;
    }

    if (latestCheckinAt && checkoutTime < Date.parse(latestCheckinAt)) {
      setFieldError(
        `L'orario di uscita non può precedere l'ultimo arrivo selezionato (${formatDateTime(latestCheckinAt)}).`,
      );
      return;
    }

    setFieldError(null);
    await onConfirm(checkoutAtIso);
  }

  return (
    <Modal
      title={
        records.length === 1
          ? "Registra uscita"
          : `Registra uscita per ${records.length} visite`
      }
      onClose={onClose}
    >
      <form className="visit-edit-form" onSubmit={handleSubmit}>
        <ul className="close-visits-list">
          {listedRecords.map((record) => (
            <li key={record.visitId}>
              <strong>
                {record.firstName} {record.lastName}
              </strong>
              <span>
                {record.company} · arrivo {formatDateTime(record.checkinAt)}
              </span>
            </li>
          ))}
          {hiddenCount > 0 ? <li className="muted">e altre {hiddenCount} visite</li> : null}
        </ul>
        <Field
          label="Orario di uscita"
          error={fieldError ?? undefined}
          inputProps={{
            type: "datetime-local",
            value: checkoutAtLocal,
            max: toDateTimeLocalInputValue(new Date()),
            min: latestCheckinAt ? toDateTimeLocalInputValue(latestCheckinAt) : undefined,
            onChange: (event) => setCheckoutAtLocal(event.target.value),
          }}
        />
        <StatusBanner
          tone="info"
          message="L'uscita viene registrata senza firma del visitatore: la visita risulterà “Chiusa da admin” e l'operazione resta associata al tuo utente."
        />
        {errorMessage ? <StatusBanner tone="error" message={errorMessage} /> : null}
        <div className="toolbar-actions toolbar-actions--end">
          <button className="ghost-button" type="button" onClick={onClose}>
            Annulla
          </button>
          <button className="primary-button" type="submit" disabled={isSaving}>
            {isSaving ? "Registrazione..." : "Conferma uscita"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
